// #genai
import React, { useState } from 'lib/react';
import PropTypes from 'prop-types';
import { useTranslation } from 'lib/translation';
import {
  EVTooltip,
  EVInfoOutlinedIcon,
  EVClear,
  EVIconButton,
  EVClickAwayListener,
  makeStyles,
} from 'lib/components';
import Typography from 'commonComponents/typography';
import { getCurrencySymbol } from 'features/order/Order.utils';
import { SUBSCRIPTION_STATUS_COLORS } from '../OrderHistory.constants';

// #genai - Custom styles for tooltip
const useStyles = makeStyles((theme) => ({
  tooltip: {
    backgroundColor: theme.evPalette.salt[100],
    padding: 0,
    maxWidth: '280px',
    boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.15)',
    borderRadius: '0px',
    pointerEvents: 'auto',
    marginTop: '16px',
    marginBottom: '0px',
  },
  arrow: {
    color: theme.evPalette.salt[100],
  },
}));

function SubscriptionCell({ subscriptionDetailsData, currencyTypeId }) {
  const { t } = useTranslation();
  const classes = useStyles();
  const [open, setOpen] = useState(false);

  const handleTooltipOpen = () => {
    setOpen(true);
  };

  const handleTooltipClose = () => {
    setOpen(false);
  };

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      border: '1px solid #E0E0E0',
      borderRadius: '4px',
      width: '180px',
      alignItems: 'center',
      justifyContent: 'center',
    }}
    >
      <Typography
        variant="caption-700"
        fontColor="charcoal-800"
        style={{
          textTransform: 'uppercase',
          textAlign: 'center',
          padding: '8px 8px 4px 8px',
          wordBreak: 'break-word',
          lineHeight: '1.3',
        }}
      >
        {subscriptionDetailsData.subscriptionSku}
      </Typography>
      <div style={{
        padding: '4px 8px',
        backgroundColor: SUBSCRIPTION_STATUS_COLORS[subscriptionDetailsData.status]?.background || '#F0F2F7',
        fontSize: '12px',
        fontWeight: 500,
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        borderTop: '1px solid #C8CDD7',
      }}
      >

        {subscriptionDetailsData.individualCost > 0 && (
          <Typography variant="label-600" fontColor="charcoal-800">
            {getCurrencySymbol(currencyTypeId)}
            {subscriptionDetailsData.individualCost}
          </Typography>
        )}

        {subscriptionDetailsData.status !== 'N/A' && (
          <Typography
            variant="label-600"
            style={{
              color: SUBSCRIPTION_STATUS_COLORS[subscriptionDetailsData.status].text,
            }}
          >
            {subscriptionDetailsData.status}
          </Typography>
        )}
        {(subscriptionDetailsData.overageRate > 0 || subscriptionDetailsData.integrationFee > 0 || subscriptionDetailsData.deliveryFee > 0) && (
          <div
            style={{
              padding: '4px 8px',
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'white',
              borderRadius: '8px',
              marginTop: '4px',
              gap: '4px',
            }}
          >
            <Typography variant="label-600" style={{ color: SUBSCRIPTION_STATUS_COLORS[subscriptionDetailsData.status].text }}>
              {t('order_history__subscription__additional_fees')}
            </Typography>
            <Typography variant="label-600" style={{ color: SUBSCRIPTION_STATUS_COLORS[subscriptionDetailsData.status].amount }}>
              {getCurrencySymbol(currencyTypeId)}
              {subscriptionDetailsData.overageRate + subscriptionDetailsData.integrationFee + subscriptionDetailsData.deliveryFee}
            </Typography>
            <EVClickAwayListener onClickAway={handleTooltipClose}>
              <div style={{ display: 'inline-flex' }}>
                <EVTooltip
                  arrow
                  open={open}
                  disableFocusListener
                  disableHoverListener
                  disableTouchListener
                  placement="bottom"
                  classes={{ tooltip: classes.tooltip, arrow: classes.arrow }}
                  title={(
                    <div style={{
                      display: 'flex', flexDirection: 'column', gap: '16px', minWidth: '220px', pointerEvents: 'auto', padding: '8px',
                    }}
                    >
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                      >
                        <Typography
                          variant="body-500"
                          style={{ fontWeight: 600, fontSize: '16px', color: '#1A1A1A' }}
                        >
                          {t('order_history__subscription__additional_fees')}
                        </Typography>
                        <EVIconButton
                          aria-label="close"
                          size="small"
                          onClick={handleTooltipClose}
                          style={{ padding: '4px' }}
                        >
                          <EVClear style={{ width: '18px', height: '18px', color: '#6B7280' }} />
                        </EVIconButton>
                      </div>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                          {subscriptionDetailsData.overageRate > 0 && (
                          <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                          }}
                          >
                            <Typography
                              variant="label-600"
                              fontColor="charcoal-700"
                              style={{
                                textTransform: 'uppercase',
                              }}
                            >
                              {t('subscription_dashboard__overage')}
                            </Typography>
                            <Typography variant="label-400" fontColor="charcoal-900">
                              {getCurrencySymbol(currencyTypeId)}
                              {subscriptionDetailsData.overageRate}
                            </Typography>
                          </div>
                          )}
                          {subscriptionDetailsData.integrationFee > 0 && (
                          <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                          }}
                          >
                            <Typography
                              variant="label-600"
                              fontColor="charcoal-700"
                              style={{
                                textTransform: 'uppercase',
                              }}
                            >
                              {t('subscription_info__integration_fee')}
                            </Typography>
                            <Typography variant="label-400" fontColor="charcoal-900">
                              {getCurrencySymbol(currencyTypeId)}
                              {subscriptionDetailsData.integrationFee}
                            </Typography>
                          </div>
                          )}
                          {subscriptionDetailsData.deliveryFee > 0 && (
                          <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                          }}
                          >
                            <Typography
                              variant="label-600"
                              fontColor="charcoal-700"
                              style={{
                                textTransform: 'uppercase',
                              }}
                            >
                              {t('subscription_info__expedited_delivery')}
                            </Typography>
                            <Typography variant="label-400" fontColor="charcoal-900">
                              {getCurrencySymbol(currencyTypeId)}
                              {subscriptionDetailsData.deliveryFee}
                            </Typography>
                          </div>
                          )}
                        </div>
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          borderTop: '1px solid #DCE0E9',
                          padding: '8px 0px',
                        }}
                        >
                          <Typography style={{
                            color: '#1A1A1A', fontSize: '14px', fontWeight: 600, textTransform: 'uppercase',
                          }}
                          >
                            {t('consumption__total')}
                          </Typography>
                          <Typography style={{ color: '#1A1A1A', fontSize: '14px', fontWeight: 600 }}>
                            {getCurrencySymbol(currencyTypeId)}
                            {subscriptionDetailsData.overageRate + subscriptionDetailsData.integrationFee + subscriptionDetailsData.deliveryFee}
                          </Typography>
                        </div>
                      </div>
                    </div>
                  )}
                >
                  {/* #genai Use a style-reset native button while preserving the original Enter-only keyboard behavior. */}
                  <button
                    type="button"
                    onClick={handleTooltipOpen}
                    onKeyDown={(e) => {
                      if (e.key === ' ') e.preventDefault();
                    }}
                    style={{
                      appearance: 'none',
                      display: 'inline-flex',
                      cursor: 'pointer',
                      padding: 0,
                      margin: 0,
                      border: 'none',
                      background: 'none',
                      font: 'inherit',
                      color: 'inherit',
                      lineHeight: 'inherit',
                    }}
                  >
                    <EVInfoOutlinedIcon
                      htmlColor={SUBSCRIPTION_STATUS_COLORS[subscriptionDetailsData.status].amount}
                      style={{ width: '14px', height: '14px' }}
                    />
                  </button>
                </EVTooltip>
              </div>
            </EVClickAwayListener>
          </div>
        )}

      </div>
    </div>
  );
}

SubscriptionCell.propTypes = {
  subscriptionDetailsData: PropTypes.shape({
    subscriptionSku: PropTypes.string,
    status: PropTypes.string,
    individualCost: PropTypes.number,
    overageRate: PropTypes.number,
    integrationFee: PropTypes.number,
    deliveryFee: PropTypes.number,
  }),
  currencyTypeId: PropTypes.number,
};

SubscriptionCell.defaultProps = {
  subscriptionDetailsData: null,
  currencyTypeId: 1,
};

export default SubscriptionCell;
